import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { verificationService } from '../services/verificationService';
import VerificationBadge from '../components/VerificationBadge';
import '../styles/VerificationPage.css';

const VerificationPage = () => {
  const { user } = useAuth();
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [phone, setPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showPhoneForm, setShowPhoneForm] = useState(false);
  const [showCodeForm, setShowCodeForm] = useState(false);

  useEffect(() => {
    if (user) {
      loadVerificationStatus();
    }
  }, [user]);

  const loadVerificationStatus = async () => {
    try {
      setLoading(true);
      const response = await verificationService.getVerificationStatus();
      if (response.status === 'success') {
        setVerificationStatus(response.data);
      }
    } catch (err) {
      setError('Failed to load verification status');
      console.error('Error loading verification status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    try {
      const response = await verificationService.verifyEmail();
      if (response.status === 'success') {
        await loadVerificationStatus();
        alert('Email verified successfully!');
      }
    } catch (err) {
      alert('Failed to verify email');
      console.error('Error verifying email:', err);
    }
  };

  const handleUpdatePhone = async () => {
    try {
      const response = await verificationService.updatePhone(phone);
      if (response.status === 'success') {
        setShowPhoneForm(false);
        setShowCodeForm(true);
        alert('Phone number updated! Please enter the verification code.');
      }
    } catch (err) {
      alert('Failed to update phone number');
      console.error('Error updating phone:', err);
    }
  };

  const handleVerifyPhone = async () => {
    try {
      const response = await verificationService.verifyPhone(verificationCode);
      if (response.status === 'success') {
        setShowCodeForm(false);
        setVerificationCode('');
        await loadVerificationStatus();
        alert('Phone verified successfully!');
      }
    } catch (err) {
      alert('Failed to verify phone');
      console.error('Error verifying phone:', err);
    }
  };

  const handleVerifyId = async () => {
    try {
      const response = await verificationService.verifyId();
      if (response.status === 'success') {
        await loadVerificationStatus();
        alert('ID verified successfully!');
      }
    } catch (err) {
      alert('Failed to verify ID');
      console.error('Error verifying ID:', err);
    }
  };

  if (loading) return <div className="loading">Loading verification status...</div>;
  if (error) return <div className="error">{error}</div>;

  const { verification_status, verification_level, is_verified } = verificationStatus || {};

  return (
    <div className="verification-page">
      <div className="verification-header">
        <h1>Account Verification</h1>
        <p>Complete your verification to build trust with other users</p>
      </div>

      <div className="verification-status">
        <h2>Current Status</h2>
        <div className="status-overview">
          <div className="verification-level">
            <span className="level-label">Verification Level:</span>
            <span className={`level-badge ${verification_level}`}>
              {verification_level?.toUpperCase() || 'NONE'}
            </span>
          </div>
          {verification_status && (
            <VerificationBadge 
              verificationStatus={verification_status} 
              size="large" 
            />
          )}
        </div>
      </div>

      <div className="verification-options">
        <h2>Verification Options</h2>
        
        {/* Email Verification */}
        <div className="verification-option">
          <div className="option-header">
            <h3>Email Verification</h3>
            {verification_status?.verified_email ? (
              <span className="status-complete">✓ Complete</span>
            ) : (
              <span className="status-incomplete">⚠ Incomplete</span>
            )}
          </div>
          <p>Verify your email address to confirm your account</p>
          {!verification_status?.verified_email && (
            <button 
              className="verify-btn email-btn"
              onClick={handleVerifyEmail}
            >
              Verify Email
            </button>
          )}
        </div>

        {/* Phone Verification */}
        <div className="verification-option">
          <div className="option-header">
            <h3>Phone Verification</h3>
            {verification_status?.verified_phone ? (
              <span className="status-complete">✓ Complete</span>
            ) : (
              <span className="status-incomplete">⚠ Incomplete</span>
            )}
          </div>
          <p>Add and verify your phone number for additional security</p>
          {!verification_status?.verified_phone && (
            <div className="phone-verification">
              {!showPhoneForm && !showCodeForm && (
                <button 
                  className="verify-btn phone-btn"
                  onClick={() => setShowPhoneForm(true)}
                >
                  Add Phone Number
                </button>
              )}
              
              {showPhoneForm && (
                <div className="phone-form">
                  <input
                    type="tel"
                    placeholder="Enter phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="phone-input"
                  />
                  <button 
                    className="verify-btn phone-btn"
                    onClick={handleUpdatePhone}
                  >
                    Submit
                  </button>
                </div>
              )}
              
              {showCodeForm && (
                <div className="code-form">
                  <input
                    type="text"
                    placeholder="Enter verification code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    className="code-input"
                  />
                  <button 
                    className="verify-btn phone-btn"
                    onClick={handleVerifyPhone}
                  >
                    Verify Code
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ID Verification */}
        <div className="verification-option">
          <div className="option-header">
            <h3>ID Verification</h3>
            {verification_status?.verified_id ? (
              <span className="status-complete">✓ Complete</span>
            ) : (
              <span className="status-incomplete">⚠ Incomplete</span>
            )}
          </div>
          <p>Upload a government-issued ID for maximum trust</p>
          {!verification_status?.verified_id && (
            <button 
              className="verify-btn id-btn"
              onClick={handleVerifyId}
            >
              Verify ID
            </button>
          )}
        </div>
      </div>

      <div className="verification-benefits">
        <h2>Benefits of Verification</h2>
        <div className="benefits-grid">
          <div className="benefit">
            <h4>🔒 Enhanced Security</h4>
            <p>Protect your account with multiple verification methods</p>
          </div>
          <div className="benefit">
            <h4>🤝 Build Trust</h4>
            <p>Show other users that you're a verified member</p>
          </div>
          <div className="benefit">
            <h4>📈 Better Experience</h4>
            <p>Access premium features and faster transactions</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationPage; 